/**
 * Meta (Facebook) Pixel standard events.
 *
 * The pixel snippet itself is per-tenant config, pasted in FMS → Website
 * Settings and injected by `analytics/marketingTags`, so `fbq` exists only on
 * the storefronts whose operator added one. Everywhere else every call here
 * is a no-op.
 *
 * Why these fire from code rather than Meta's Event Setup Tool: that tool
 * attaches an event to a DOM click it finds when it scans the page, and the
 * checkout button fails both halves of that. It is rendered only after the
 * payment config (`ourCompany.default_payment`, then Stripe availability)
 * resolves, so it is usually not in the DOM when the tool scans; and a click
 * on it is not a payment — a declined card would still be counted as a
 * Purchase, with no value attached. Mirroring the funnel events we already
 * send means Meta sees the payment, with its amount, when it actually
 * succeeds.
 */

/** Our funnel event -> Meta standard event. Anything unmapped is not sent. */
const STANDARD_EVENTS: Record<string, string> = {
  vehicle_listing_viewed: "ViewContent",
  booking_started: "InitiateCheckout",
  booking_completed: "Purchase",
};

export function trackMetaEvent(
  eventName: string,
  properties?: Record<string, any>
): void {
  const standard = STANDARD_EVENTS[eventName];
  const fbq = (window as any).fbq;
  if (!standard || typeof fbq !== "function") return;

  const props = properties ?? {};
  const value = Number(props.value);
  const payload: Record<string, any> = {};

  if (props.vehicle_id) {
    payload.content_type = "product";
    payload.content_ids = [String(props.vehicle_id)];
  }
  if (props.vehicle_name) payload.content_name = props.vehicle_name;
  // A missing or unusable amount still sends the event — Meta counts it, it
  // just cannot optimise on revenue. Sending `value: NaN` would be worse.
  if (Number.isFinite(value) && value > 0) {
    // `value` arrives as whatever the API sent — the price fields are DRF
    // decimals, i.e. strings like "394.00" — and `currency` is lowercase
    // ("usd") on /api/company/configurations/, where Meta wants ISO 4217.
    payload.value = value;
    payload.currency = String(props.currency || "USD").toUpperCase();
  }

  // `fbq` is the operator's own pasted snippet, and this runs on the payment
  // success path — the Purchase call sits between a charged card and the
  // redirect to /payment-success. A broken vendor snippet must cost us an
  // analytics event, never the renter's confirmation.
  try {
    fbq("track", standard, payload);
  } catch (error) {
    console.error("[Meta] pixel event failed", standard, error);
  }
}
